import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { DELETE_PROFILE } from '@web/src/features/account/account-delete-modal/account-delete-modal'
import { useTranslation } from '@web/src/lib/intl/react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileNameField from 'src/components/primatives/sf-profile-name-field/sf-profile-name-field'
import type {
  AdminUpdateProfileNameMutation,
  AdminUpdateProfileNameMutationVariables,
  UndeleteProfileMutation,
  UndeleteProfileMutationVariables,
} from 'types/graphql'
import { useAuth } from '../../../../hooks/use-auth'

export const UPDATE_PROFILE_NAME: TypedDocumentNode<
  AdminUpdateProfileNameMutation,
  AdminUpdateProfileNameMutationVariables
> = gql`
  mutation AdminUpdateProfileName($id: String!, $name: String!) {
    updateProfileName(id: $id, name: $name) {
      id
      name
    }
  }
`

export const UNDELETE_PROFILE: TypedDocumentNode<UndeleteProfileMutation, UndeleteProfileMutationVariables> = gql`
  mutation UndeleteProfile($id: String!) {
    undeleteProfile(id: $id) {
      id
      name
      deletedAt
    }
  }
`

interface UserProps {
  id: string
  email?: string
  roles?: string
  roleExpiresAt?: string
  emailVerified?: boolean
  profile?: {
    id: string
    name: string
    deletedAt?: string
  }
}

interface ProfileEditProps {
  user: UserProps
  className?: string
  onProfileUpdated?: () => void | Promise<void>
}

const ProfileEdit = forwardRef<HTMLDivElement, ProfileEditProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const formRef = useRef<HTMLFormElement>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [saveModal, setSaveModal] = useState(false)
  const { user, className, onProfileUpdated } = props

  const formMethods = useForm({
    defaultValues: {
      action: 'none',
      profileName: user?.profile?.name,
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    formMethods.reset({
      action: 'none',
      profileName: user?.profile?.name,
    })
  }, [user?.profile?.name, formMethods])

  const [updateProfileName] = useMutation(UPDATE_PROFILE_NAME, {
    onCompleted: async () => {
      toast.success(t('Profile Name Changed'))
      setShowSpinnerValue(false)
      await onProfileUpdated?.()
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const [deleteProfile] = useMutation(DELETE_PROFILE, {
    onCompleted: async () => {
      toast.success(t('Profile Deleted'))
      setShowSpinnerValue(false)
      await onProfileUpdated?.()
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const [undeleteProfile] = useMutation(UNDELETE_PROFILE, {
    onCompleted: async () => {
      toast.success(t('Profile Undeleted'))
      setShowSpinnerValue(false)
      await onProfileUpdated?.()
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const handleSubmit = (fieldData: { action: string; profileName: string }) => {
    setShowSpinnerValue(true)
    setSaveModal(false)

    if (fieldData.action === 'deleteProfile') {
      deleteProfile({
        variables: {
          id: user.profile.id,
          deletionInput: {
            reason: 'OTHER',
            customReason: t('{{name}} deleted this profile', { name: currentUser.profile.name }),
          },
        },
      })
    } else if (fieldData.action === 'undeleteProfile') {
      undeleteProfile({ variables: { id: user.profile.id } })
    } else if (fieldData.action === 'save') {
      updateProfileName({ variables: { id: user.profile.id, name: fieldData.profileName } })
    }
  }

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit} ref={formRef} style={{ width: '100%' }}>
      <div className={cn('flex w-full flex-col gap-4', className)}>
        <Card className="w-full" ref={forwardedRef}>
          <CardHeader>
            <CardTitle>{t('Profile Details')}</CardTitle>
          </CardHeader>
          <CardContent>
            <SFProfileNameField
              defaultValue={user?.profile?.name}
              label={t('Profile Name')}
              name="profileName"
              readOnly={Boolean(user?.profile?.deletedAt)}
              required
            />
          </CardContent>
        </Card>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          {user.profile?.deletedAt ? (
            <Button
              disabled={showSpinner}
              onClick={() => {
                formMethods.setValue('action', 'undeleteProfile')
                setSaveModal(true)
              }}
              type="button"
              variant="secondary"
            >
              {showSpinner ? <Spinner className="size-4" /> : null}
              Undelete Profile
            </Button>
          ) : (
            <Button
              disabled={showSpinner}
              onClick={() => {
                formMethods.setValue('action', 'deleteProfile')
                setSaveModal(true)
              }}
              type="button"
              variant="destructive"
            >
              Delete Profile
            </Button>
          )}
          <div className="flex-1" />
          <Button
            disabled={!isDirty || showSpinner}
            onClick={() => {
              formMethods.setValue('action', 'save')
              setSaveModal(true)
            }}
            type="button"
          >
            {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
            Save
          </Button>
        </div>
      </div>

      <ConfirmDialog
        confirmText={t('Yes')}
        onConfirm={() => {
          formRef.current?.requestSubmit()
        }}
        onOpenChange={(e) => setSaveModal(e.open)}
        open={saveModal}
        text={t('Are you sure you want to do this?')}
        title={t('Are you sure?')}
        variant="basic"
      />
    </Form>
  )
})

ProfileEdit.displayName = 'ProfileEdit'

export default ProfileEdit
